import Component from '@glimmer/component';
import { variation } from 'ember-launch-darkly';
import { eq } from 'ember-truth-helpers';
import { HdsIcon } from '@hashicorp/design-system-components/components';
import { Flex } from 'core/components';
import { optionForSourceState } from 'core/helpers';
import hvnRouteTargetTypeLabel from 'hcp/helpers/hvn-route-target-type-label';

import type {
  HashicorpCloudNetwork20200907HVNRoute,
  HashicorpCloudNetwork20200907Network,
} from '@clients/cloud-network';

interface HvnRoutesTableTargetTypeLabelSignature {
  Args: {
    route: HashicorpCloudNetwork20200907HVNRoute;
    network: HashicorpCloudNetwork20200907Network;
  };
  Element: HTMLDivElement;
}

/**
 *
 * `HvnRoutesTableTargetTypeLabel` renders the target type label and icon.
 *
 * If the route is making use of Azure hub/spoke topology, the label will show the
 * route's Azure next hop type and next hop IP address.
 *
 * Otherwise, the label will show the same display value as the resource-type-display-name helper.
 *
 *
 * ```
 * <Page::HvnRoutes::Table::TargetTypeLabel
 *   @route={{route}}
 *   @network={{network}}
 * />
 * ```
 *
 * @class HvnRoutesTableTargetTypeLabel
 *
 */
export default class HvnRoutesTableTargetTypeLabel extends Component<HvnRoutesTableTargetTypeLabelSignature> {
  get nextHopIp() {
    const { route } = this.args;
    if (variation('hcp-ui-azure-hub-spoke')) {
      return route?.azureRoute?.nextHopIpAddress;
    }
  }
  <template>
    <Flex
      @alignItems="center"
      @direction="row"
      @gap="2xs"
      data-test-hvn-routes-table-target-type-label
    >
      {{#let (hvnRouteTargetTypeLabel @route) as |label|}}
        {{#let
          (eq @route.target.hvnConnection.type "hashicorp.network.hvn")
          (optionForSourceState "region" @network.location.region.provider)
          as |isHvnTypeConnection option|
        }}
          <HdsIcon
            @name={{if isHvnTypeConnection "hashicorp-color" option.icon}}
            @isInline={{true}}
            data-test-provider-logo={{if
              isHvnTypeConnection
              "hashicorp-color"
              @network.location.region.provider
            }}
          />
          <Flex @direction="column" @gap="xxs">
            <span data-test-hvn-routes-table-target-type-label-primary>
              {{label}}
            </span>
            {{#if this.nextHopIp}}
              <span
                class="hds-typography-display-100 hds-font-weight-regular hds-foreground-faint"
                data-test-hvn-routes-table-target-type-label-ip
              >
                {{this.nextHopIp}}
              </span>
            {{/if}}
          </Flex>
        {{/let}}
      {{/let}}
    </Flex>
  </template>
}
